import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Group,
  ActionIcon,
  Tooltip,
  Box,
  Menu,
  Button,
  Text,
  Anchor,
  Divider,
} from '@mantine/core';
import {
  IconBold,
  IconItalic,
  IconUnderline,
  IconStrikethrough,
  IconList,
  IconListNumbers,
  IconH1,
  IconH2,
  IconClearFormatting,
  IconDownload,
  IconCheck,
  IconFileTypeTxt,
  IconFileTypeDoc,
  IconFileTypePdf,
} from '@tabler/icons-react';

export interface RichTextNotesEditorProps {
  /** Raw HTML — comes from the storage hook. */
  value: string;
  onChange: (next: string) => void;
  savedAt?: number | null;
  onClear?: () => void;
  /** Used as the export filename and the page title in the printed PDF. */
  exportName?: string;
  placeholder?: string;
}

/**
 * Lightweight rich-text editor backed by <c>contenteditable</c> +
 * <c>document.execCommand</c>. We deliberately avoid pulling in tiptap (~80KB
 * gzipped) because notes are a personal scratch space, not a full CMS — the
 * native commands cover bold/italic/underline/lists/headings.
 *
 * Output is HTML, which round-trips cleanly through the existing localStorage
 * hook. Three exporters: TXT (tag-stripped), DOC (HTML wrapped in MSO header
 * — Word opens it natively), and PDF (print the document via a hidden iframe
 * — every browser exposes "Save as PDF" in its print dialog).
 */
export function RichTextNotesEditor({
  value,
  onChange,
  savedAt,
  onClear,
  exportName = 'session-notes',
  placeholder = "Write your notes here. They auto-save and stay private to you on this device.",
}: RichTextNotesEditorProps) {
  const editorRef = useRef<HTMLDivElement | null>(null);
  // Track whether we've already populated the DOM for the current `value`.
  // Without this we'd reset the cursor on every keystroke.
  const isUserTypingRef = useRef(false);
  const [charCount, setCharCount] = useState(0);

  // Sync external value into the contenteditable (e.g. on first mount or when
  // the parent loads new notes for a different session).
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (isUserTypingRef.current) {
      isUserTypingRef.current = false;
      return;
    }
    if (el.innerHTML !== value) {
      el.innerHTML = value ?? '';
    }
    setCharCount(el.innerText.length);
  }, [value]);

  const exec = useCallback((command: string, arg?: string) => {
    document.execCommand(command, false, arg);
    editorRef.current?.focus();
    handleInput();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInput = () => {
    const el = editorRef.current;
    if (!el) return;
    isUserTypingRef.current = true;
    onChange(el.innerHTML);
    setCharCount(el.innerText.length);
  };

  const exportAsTxt = () => {
    const text = stripHtml(value);
    downloadBlob(`${exportName}.txt`, new Blob([text], { type: 'text/plain;charset=utf-8' }));
  };

  const exportAsDoc = () => {
    // Word recognises this MSO-flavoured HTML and opens it as a .doc
    // document. No mammoth.js / docx deps needed.
    const html = `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="utf-8"><title>${escapeHtml(exportName)}</title></head>
<body style="font-family: Calibri, Arial, sans-serif; line-height: 1.5;">
${value || '<p></p>'}
</body></html>`;
    downloadBlob(`${exportName}.doc`, new Blob(['﻿', html], { type: 'application/msword' }));
  };

  const exportAsPdf = () => {
    // Render notes in a hidden iframe and trigger the browser print dialog —
    // every modern browser ships a "Save as PDF" destination there.
    const frame = document.createElement('iframe');
    frame.style.position = 'fixed';
    frame.style.right = '0';
    frame.style.bottom = '0';
    frame.style.width = '0';
    frame.style.height = '0';
    frame.style.border = '0';
    document.body.appendChild(frame);
    const doc = frame.contentDocument;
    if (!doc) {
      document.body.removeChild(frame);
      return;
    }
    doc.open();
    doc.write(`<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>${escapeHtml(exportName)}</title>
<style>
  body { font-family: -apple-system, system-ui, "Segoe UI", Roboto, sans-serif; line-height: 1.6; padding: 32px; color: #111; }
  h1, h2, h3 { margin-top: 1.2em; }
  ul, ol { padding-left: 1.4em; }
  blockquote { border-left: 3px solid #ddd; margin: 0.6em 0; padding-left: 1em; color: #555; }
</style></head>
<body>${value || '<p></p>'}</body></html>`);
    doc.close();
    // Give the iframe a tick to lay out before printing.
    window.setTimeout(() => {
      try {
        frame.contentWindow?.focus();
        frame.contentWindow?.print();
      } finally {
        // Leave the iframe attached briefly so the print dialog has time to
        // grab a snapshot, then clean up.
        window.setTimeout(() => document.body.removeChild(frame), 1000);
      }
    }, 50);
  };

  return (
    <Box
      style={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid var(--mantine-color-default-border)',
        borderRadius: 'var(--mantine-radius-md)',
        overflow: 'hidden',
        background: 'var(--mantine-color-body)',
      }}
    >
      {/* Toolbar */}
      <Group
        gap={2}
        px={6}
        py={4}
        wrap="nowrap"
        style={{
          borderBottom: '1px solid var(--mantine-color-default-border)',
          background: 'var(--mantine-color-default-hover)',
          flex: '0 0 auto',
          overflowX: 'auto',
        }}
      >
        <FormatButton label="Bold" icon={<IconBold size={14} />} onClick={() => exec('bold')} />
        <FormatButton label="Italic" icon={<IconItalic size={14} />} onClick={() => exec('italic')} />
        <FormatButton label="Underline" icon={<IconUnderline size={14} />} onClick={() => exec('underline')} />
        <FormatButton label="Strikethrough" icon={<IconStrikethrough size={14} />} onClick={() => exec('strikeThrough')} />
        <Divider orientation="vertical" mx={4} />
        <FormatButton label="Heading 1" icon={<IconH1 size={14} />} onClick={() => exec('formatBlock', '<h1>')} />
        <FormatButton label="Heading 2" icon={<IconH2 size={14} />} onClick={() => exec('formatBlock', '<h2>')} />
        <Divider orientation="vertical" mx={4} />
        <FormatButton label="Bullet list" icon={<IconList size={14} />} onClick={() => exec('insertUnorderedList')} />
        <FormatButton label="Numbered list" icon={<IconListNumbers size={14} />} onClick={() => exec('insertOrderedList')} />
        <Divider orientation="vertical" mx={4} />
        <FormatButton label="Clear formatting" icon={<IconClearFormatting size={14} />} onClick={() => exec('removeFormat')} />

        <Box style={{ flex: 1 }} />

        <Menu position="bottom-end" withinPortal>
          <Menu.Target>
            <Button size="compact-xs" variant="light" leftSection={<IconDownload size={12} />}>
              Export
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label>Save notes as</Menu.Label>
            <Menu.Item leftSection={<IconFileTypeTxt size={14} />} onClick={exportAsTxt}>
              Plain text (.txt)
            </Menu.Item>
            <Menu.Item leftSection={<IconFileTypeDoc size={14} />} onClick={exportAsDoc}>
              Word document (.doc)
            </Menu.Item>
            <Menu.Item leftSection={<IconFileTypePdf size={14} />} onClick={exportAsPdf}>
              PDF (via print)
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>

      {/* Editor surface */}
      <Box
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        data-placeholder={placeholder}
        style={{
          flex: 1,
          minHeight: 0,
          padding: 'var(--mantine-spacing-md)',
          overflowY: 'auto',
          outline: 'none',
          fontFamily: 'var(--mantine-font-family)',
          lineHeight: 1.6,
          fontSize: 14,
        }}
      />

      {/* Footer */}
      <Group
        justify="space-between"
        px="sm"
        py={6}
        style={{
          borderTop: '1px solid var(--mantine-color-default-border)',
          flex: '0 0 auto',
        }}
      >
        <Group gap={6}>
          <IconCheck size={12} color="var(--mantine-color-teal-5)" />
          <Text size="xs" c="dimmed">
            {savedAt ? `Saved ${relativeTime(savedAt)} — only on this device` : 'Auto-saving as you type'}
          </Text>
        </Group>
        <Group gap="md">
          <Text size="xs" c="dimmed">
            {charCount} char{charCount === 1 ? '' : 's'}
          </Text>
          {onClear && charCount > 0 && (
            <Anchor
              component="button"
              type="button"
              size="xs"
              c="red"
              onClick={() => {
                if (confirm('Clear notes for this session?')) onClear();
              }}
            >
              Clear
            </Anchor>
          )}
        </Group>
      </Group>

      {/* Placeholder styling — only shows when the contenteditable is empty. */}
      <style>{`
        [contenteditable][data-placeholder]:empty::before {
          content: attr(data-placeholder);
          color: var(--mantine-color-dimmed);
          pointer-events: none;
        }
      `}</style>
    </Box>
  );
}

function FormatButton({ label, icon, onClick }: { label: string; icon: React.ReactNode; onClick: () => void }) {
  return (
    <Tooltip label={label} openDelay={400}>
      <ActionIcon variant="subtle" size="sm" onClick={onClick} aria-label={label}>
        {icon}
      </ActionIcon>
    </Tooltip>
  );
}

function stripHtml(html: string): string {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  // Replace block boundaries with newlines so paragraphs/list items survive.
  tmp.querySelectorAll('br').forEach((el) => el.replaceWith('\n'));
  tmp.querySelectorAll('p, div, li, h1, h2, h3, h4, h5, h6').forEach((el) => {
    el.append('\n');
  });
  return tmp.innerText.replace(/\n{3,}/g, '\n\n').trim();
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
}

function downloadBlob(filename: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function relativeTime(ts: number): string {
  const diff = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (diff < 5) return 'just now';
  if (diff < 60) return `${diff}s ago`;
  const m = Math.floor(diff / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return new Date(ts).toLocaleString();
}
